import { fitImageDimensions } from './imageUpload.js';

const cache = new WeakMap();

export async function prepareVideoUpload(file) {
  if (file?.type !== 'video/mp4' || !file.size) return file;
  if (!cache.has(file)) cache.set(file, compressVideo(file));
  return cache.get(file);
}

async function compressVideo(file) {
  let video, canvas, url, stream, audio, recorder, drawTimer, timeout, cancel;
  try {
    if (!globalThis.MediaRecorder?.isTypeSupported('video/mp4') || document.hidden) return file;
    const AudioContext = globalThis.AudioContext || globalThis.webkitAudioContext;
    if (!AudioContext) return file;
    canvas = document.createElement('canvas');
    if (!canvas.captureStream) return file;
    video = document.createElement('video');
    // Audio is routed exclusively into a MediaStream below, never to speakers.
    // Muting the element would also silence the audio in the encoded file.
    video.muted = false;
    video.playsInline = true;
    video.preload = 'auto';
    url = URL.createObjectURL(file);
    await new Promise((resolve, reject) => {
      timeout = setTimeout(() => reject(new Error('Video decode timed out')), 10000);
      video.onloadeddata = resolve;
      video.onerror = () => reject(new Error('Video decode failed'));
      video.src = url;
    });
    clearTimeout(timeout);
    // The server accepts short clips only; never silently trim longer videos.
    if (!Number.isFinite(video.duration) || video.duration <= 0 || video.duration > 10 || !video.videoWidth) return file;
    const scale = Math.min(1, 1280 / Math.max(video.videoWidth, video.videoHeight), 720 / Math.min(video.videoWidth, video.videoHeight));
    const size = fitImageDimensions(video.videoWidth, video.videoHeight, Math.max(video.videoWidth, video.videoHeight) * scale);
    // H.264 encoders generally require even dimensions.
    canvas.width = Math.max(2, Math.floor(size.width / 2) * 2);
    canvas.height = Math.max(2, Math.floor(size.height / 2) * 2);
    const context = canvas.getContext('2d');
    if (!context) return file;
    const draw = () => context.drawImage(video, 0, 0, canvas.width, canvas.height);
    draw();
    stream = canvas.captureStream(24);
    // Route the original audio into the recording without playing it through speakers.
    audio = new AudioContext();
    const destination = audio.createMediaStreamDestination();
    audio.createMediaElementSource(video).connect(destination);
    destination.stream.getAudioTracks().forEach(track => stream.addTrack(track));
    await new Promise((resolve, reject) => {
      timeout = setTimeout(() => reject(new Error('Audio initialization timed out')), 3000);
      audio.resume().then(resolve, reject);
    });
    clearTimeout(timeout);
    recorder = new MediaRecorder(stream, { mimeType: 'video/mp4', videoBitsPerSecond: 1000000, audioBitsPerSecond: 64000 });
    const chunks = [];
    const blob = await new Promise((resolve, reject) => {
      let ended = false;
      cancel = () => { if (document.hidden) reject(new Error('Video processing interrupted')); };
      document.addEventListener('visibilitychange', cancel);
      timeout = setTimeout(() => reject(new Error('Video encoding timed out')), 25000);
      recorder.ondataavailable = event => { if (event.data.size) chunks.push(event.data); };
      recorder.onerror = () => reject(new Error('Video encoding failed'));
      recorder.onstop = () => ended ? resolve(new Blob(chunks, { type: 'video/mp4' })) : reject(new Error('Video encoding interrupted'));
      video.onerror = () => reject(new Error('Video playback failed'));
      video.onended = () => { ended = true; draw(); recorder.stop(); };
      drawTimer = setInterval(draw, 1000 / 24);
      recorder.start();
      video.play().catch(reject);
    });
    if (!blob.size || blob.size >= file.size) return file;
    const result = new File([blob], file.name, { type: 'video/mp4', lastModified: file.lastModified });
    cache.set(result, Promise.resolve(result));
    return result;
  } catch {
    // Preserve the original (including audio) if MP4 encoding is unavailable.
    return file;
  } finally {
    clearTimeout(timeout);
    clearInterval(drawTimer);
    if (cancel) document.removeEventListener('visibilitychange', cancel);
    if (recorder?.state === 'recording') { recorder.onstop = null; recorder.stop(); }
    stream?.getTracks().forEach(track => track.stop());
    if (audio) await audio.close().catch(() => {});
    if (video) { video.pause(); video.removeAttribute('src'); video.load(); }
    if (url) URL.revokeObjectURL(url);
    if (canvas) { canvas.width = 0; canvas.height = 0; }
  }
}
